import { useEffect, useMemo, useRef, useState } from 'react'
import { marked } from '../utils/marked'
import { chatStream, getForecast, getModels, getQuestion } from '../api/client'
import type { ChatRequest, ForecastRun } from '../types'
import { useForecastStore } from '../store/forecastStore'
import { useSessionStore } from '../store/sessionStore'
import { useUIStore } from '../store/uiStore'
import './AnalysisChatWidget.css'

type ChatMsg = { role: 'user' | 'assistant'; content: string }
type SearchEvent = { query: string; status: 'searching' | 'complete'; results?: Array<{ title?: string; url?: string }> }

function readSseStream(
  stream: ReadableStream<Uint8Array>,
  onEvent: (payload: any) => void
): Promise<void> {
  const reader = stream.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  const pump = async (): Promise<void> => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // SSE messages are separated by blank lines
      const parts = buffer.split('\n\n')
      buffer = parts.pop() || ''
      for (const part of parts) {
        const lines = part.split('\n')
        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed.startsWith('data:')) continue
          const jsonStr = trimmed.slice(5).trim()
          if (!jsonStr) continue
          try {
            onEvent(JSON.parse(jsonStr))
          } catch {
            // ignore malformed chunks
          }
        }
      }
    }
  }

  return pump()
}

export default function AnalysisChatWidget() {
  const isOpen = useUIStore((s) => s.analysisChatOpen)
  const toggle = useUIStore((s) => s.toggleAnalysisChat)

  const currentQuestionId = useForecastStore((s) => s.currentQuestionId)
  const currentRuns = useForecastStore((s) => s.currentRuns)
  const hasModelAccess = useSessionStore((s) => s.hasModelAccess)

  // Only show the chat widget if there's at least one completed forecast
  const hasCompletedRuns = currentRuns.some(run => run.status === 'complete' && run.final_result)

  const [size, setSize] = useState<'compact' | 'large'>('compact')
  const [showSettings, setShowSettings] = useState(false)
  const [models, setModels] = useState<Array<{ id: string; name: string }>>([])
  const [model, setModel] = useState('gpt-5-nano')
  const [reasoningEffort, setReasoningEffort] = useState('low')
  const [searchDepth, setSearchDepth] = useState('low')
  const [enableWebSearch, setEnableWebSearch] = useState(true)

  const [messages, setMessages] = useState<ChatMsg[]>([])
  const [draft, setDraft] = useState('')
  const [assistantBuffer, setAssistantBuffer] = useState('')
  const [searches, setSearches] = useState<SearchEvent[]>([])
  const [isThinking, setIsThinking] = useState(false)

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const prevScrollTop = useRef<number>(0)
  const assistantBufferRef = useRef('')

  useEffect(() => {
    // Load model list for settings dropdown
    void (async () => {
      try {
        const res: any = await getModels()
        const list = (res?.models || []) as Array<{ id: string; name: string }>
        setModels(list.filter((m) => hasModelAccess(m.id)))
      } catch {
        // non-fatal
      }
    })()
  }, [hasModelAccess])

  // Preserve scroll position (never auto-scroll)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = prevScrollTop.current
  }, [messages, assistantBuffer, searches, isThinking])

  useEffect(() => {
    assistantBufferRef.current = assistantBuffer
  }, [assistantBuffer])

  const title = useMemo(() => {
    return 'AI Analysis'
  }, [])

  const buildContext = async (): Promise<any> => {
    if (!currentQuestionId) throw new Error('No forecast selected')
    const q = await getQuestion(currentQuestionId)

    // Hydrate each run so the chat gets full agent/supervisor outputs.
    const fullRuns: ForecastRun[] = await Promise.all((currentRuns || []).map((r) => getForecast(r.job_id)))

    // Reduce context size to prevent overwhelming the AI:
    // - If 1-3 runs: Include supervisor + all agent predictions (no URLs/sources)
    // - If 4 runs: Only include supervisor outputs (no agent predictions)
    // - If >=5 runs: Only include essential supervisor info (probability, executive summary, key factors)
    const runs = fullRuns.map((run) => {
      const shouldIncludeAgents = fullRuns.length <= 3
      const useTruncatedContext = fullRuns.length >= 5

      // Strip out URLs and sources from agent predictions
      const cleanedAgentPredictions = shouldIncludeAgents && run.agent_predictions
        ? run.agent_predictions.map((agent) => ({
            agent_id: agent.agent_id,
            probability: agent.probability,
            confidence: agent.confidence,
            reasoning: agent.reasoning,
            question_restatement: agent.question_restatement,
            key_evidence: agent.key_evidence,
            notes: agent.notes,
            // Exclude: visited_urls, searches
          }))
        : undefined

      // Keep final result but remove sources
      // If many predictions (>=5), only preserve essential info to avoid string limits
      const cleanedFinalResult = run.final_result
        ? useTruncatedContext
          ? {
              question: run.final_result.question,
              probability: run.final_result.probability,
              confidence: run.final_result.confidence,
              executive_summary: run.final_result.executive_summary || run.final_result.summary,
              key_factors: run.final_result.key_factors,
              // Exclude all verbose fields when there are many predictions
            }
          : {
              question: run.final_result.question,
              probability: run.final_result.probability,
              confidence: run.final_result.confidence,
              reasoning: run.final_result.reasoning,
              summary: run.final_result.summary,
              full_reasoning: run.final_result.full_reasoning,
              executive_summary: run.final_result.executive_summary,
              confidence_interval_90_low: run.final_result.confidence_interval_90_low,
              confidence_interval_90_high: run.final_result.confidence_interval_90_high,
              key_factors: run.final_result.key_factors,
              key_agreements: run.final_result.key_agreements,
              key_disagreements: run.final_result.key_disagreements,
              key_points_of_agreement: run.final_result.key_points_of_agreement,
              key_points_of_disagreement: run.final_result.key_points_of_disagreement,
              evidence_updates: run.final_result.evidence_updates,
              // Exclude: visited_sources
            }
        : undefined

      return {
        job_id: run.job_id,
        question_id: run.question_id,
        status: run.status,
        created_at: run.created_at,
        completed_at: run.completed_at,
        formatted_question: run.formatted_question,
        final_result: cleanedFinalResult,
        agent_predictions: cleanedAgentPredictions,
        // Exclude: config, stages, token_summary (not relevant for chat)
      }
    })

    // Exclude user predictions by design (we simply don't include them in the context).
    return { question: q, runs }
  }

  const send = async () => {
    const text = draft.trim()
    if (!text) return
    if (!currentQuestionId) {
      alert('Open a forecast first, then use analysis chat on the Results page.')
      return
    }

    const nextMessages: ChatMsg[] = [...messages, { role: 'user', content: text }]
    setMessages(nextMessages)
    setDraft('')
    setAssistantBuffer('')
    setSearches([])
    setShowSettings(false)
    setIsThinking(true)

    // snapshot scroll
    if (scrollRef.current) prevScrollTop.current = scrollRef.current.scrollTop

    try {
      const prediction_context = await buildContext()
      const req: ChatRequest = {
        messages: nextMessages,
        prediction_context,
        model,
        enable_web_search: enableWebSearch,
        reasoning_effort: reasoningEffort,
        search_depth: searchDepth,
      }

      const stream = await chatStream(req)
      await readSseStream(stream, (ev) => {
        if (scrollRef.current) prevScrollTop.current = scrollRef.current.scrollTop

        if (ev?.type === 'token') {
          setAssistantBuffer((prev) => prev + (ev.content || ''))
        } else if (ev?.type === 'search') {
          setSearches((prev) => [
            ...prev,
            {
              query: ev.query || '',
              status: ev.status === 'complete' ? 'complete' : 'searching',
              results: ev.results || undefined,
            },
          ])
        } else if (ev?.type === 'error') {
          setIsThinking(false)
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: `Error: ${String(ev.message || 'Unknown error')}` },
          ])
          setAssistantBuffer('')
        } else if (ev?.type === 'done') {
          setIsThinking(false)
          const final = assistantBufferRef.current
          if (final.trim()) {
            setMessages((prev) => [...prev, { role: 'assistant', content: final }])
          }
          setAssistantBuffer('')
        }
      })
    } catch (e) {
      setIsThinking(false)
      alert(`Chat failed: ${e instanceof Error ? e.message : 'Unknown error'}`)
    }
  }

  // Don't render the widget at all if there are no completed runs
  if (!hasCompletedRuns) {
    return null
  }

  if (!isOpen) {
    return (
      <button className="chat-fab" type="button" onClick={toggle} aria-label="Open AI analysis">
        <div className="chat-fab-label">AI Analysis</div>
      </button>
    )
  }

  const handleClearChat = () => {
    if (confirm('Clear all chat messages?')) {
      setMessages([])
      setAssistantBuffer('')
      setSearches([])
      assistantBufferRef.current = ''
    }
  }

  return (
    <div className={`chat-shell ${size}`}>
      <div className="chat-header">
        <div>
          <div className="chat-title">{title}</div>
          <div className="chat-subtitle">Ask questions about your forecasts and get AI-powered insights</div>
        </div>
        <div className="chat-header-actions">
          <button
            type="button"
            className="chat-header-btn"
            onClick={handleClearChat}
            title="Clear chat"
            disabled={messages.length === 0}
          >
            Clear
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="chat-settings">
          <div className="chat-settings-row">
            <label>Model</label>
            <select value={model} onChange={(e) => setModel(e.target.value)}>
              {(models.length ? models : [{ id: 'gpt-5-nano', name: 'GPT-5 Nano' }]).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
          <div className="chat-settings-row">
            <label>Reasoning</label>
            <select value={reasoningEffort} onChange={(e) => setReasoningEffort(e.target.value)}>
              <option value="minimal">Minimal</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div className="chat-settings-row">
            <label>Search depth</label>
            <select value={searchDepth} onChange={(e) => setSearchDepth(e.target.value)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div className="chat-settings-row checkbox">
            <label>
              <input
                type="checkbox"
                checked={enableWebSearch}
                onChange={(e) => setEnableWebSearch(e.target.checked)}
              />
              Enable web search
            </label>
          </div>
        </div>
      )}

      <div className="chat-body" ref={scrollRef}>
        {messages.map((m, idx) => (
          <div key={idx} className={`chat-bubble ${m.role}`}>
            <div
              className="chat-markdown"
              dangerouslySetInnerHTML={{
                __html: marked.parse(m.content, { breaks: true }),
              }}
            />
            {m.role === 'assistant' && (
              <button
                type="button"
                className="chat-copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(m.content)
                  alert('Copied to clipboard')
                }}
                title="Copy to clipboard"
              >
                Copy
              </button>
            )}
          </div>
        ))}

        {searches.length > 0 && (
          <div className="chat-searches">
            {searches.map((s, idx) => (
              <div key={idx} className="chat-search-line">
                <span className="chat-search-icon">⌕</span>
                <span className="chat-search-text">
                  {s.status === 'searching' ? `Searching: “${s.query}”` : `Search complete: “${s.query}”`}
                </span>
                {s.status === 'complete' && s.results && s.results.length > 0 && (
                  <div className="chat-search-results">
                    {s.results.slice(0, 5).map((r, rIdx) => (
                      <a
                        key={rIdx}
                        href={r.url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="chat-search-result"
                      >
                        {r.title || r.url || 'Result'}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {assistantBuffer && (
          <div className="chat-bubble assistant">
            <div
              className="chat-markdown"
              dangerouslySetInnerHTML={{ __html: marked.parse(assistantBuffer, { breaks: true }) }}
            />
          </div>
        )}

        {isThinking && !assistantBuffer && <div className="chat-thinking">Thinking…</div>}
      </div>

      <div className="chat-input">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void send()
            }
          }}
          placeholder="Ask about the forecasts shown on this page…"
          rows={2}
        />
        <button className="btn-primary" type="button" onClick={() => void send()} disabled={isThinking}>
          Send
        </button>
      </div>

      <div className="chat-footer-actions">
        <button
          type="button"
          className="chat-icon-btn"
          onClick={() => setShowSettings((v) => !v)}
          aria-label="Settings"
        >
          ⚙
        </button>
        <button
          type="button"
          className="chat-icon-btn"
          onClick={() => setSize((s) => (s === 'compact' ? 'large' : 'compact'))}
          aria-label="Toggle size"
        >
          ⤢
        </button>
        <button type="button" className="chat-icon-btn" onClick={toggle} aria-label="Close chat">
          ×
        </button>
      </div>
    </div>
  )
}


